/**
 * schemas/settlement
 * Leaving custodians who still hold company money. One alert per OPEN
 * assignment — a person with three assignments is three things to settle, not
 * one, because each has its own balance and its own consequence.
 */
import { z } from 'zod';
import { currency, currencyTotal, isoDate, isoDateNullable, minor } from '../common';
import { gateStateSchema } from '../config/gates';

export const separationSchema = z.object({
  id: z.string(),
  assignment_id: z.string(),
  holder: z.string(),
  last_day: isoDateNullable,
  /** What they are still holding on this assignment. */
  open_balance_minor: minor,
  cur: currency,
  /** `open` | `settled`. */
  state: z.string(),
  reason: z.string().nullable(),
  settled_by: z.string().nullable(),
  created_at: isoDate,
});
export type Separation = z.infer<typeof separationSchema>;

/**
 * GET /v1/settlement — verified against the live API.
 *
 * Rows are under `rows`, and `exposure` is the per-currency total still in
 * leavers' hands — a LIST, largest first, never one blended number
 * (invariant 4). The gate arrives in the body with a 200, as on the sync log.
 */
export const separationsResponseSchema = z.object({
  gates: gateStateSchema,
  rows: z.array(separationSchema),
  exposure: z.array(currencyTotal),
});
export type SettlementResponse = z.infer<typeof separationsResponseSchema>;

/** POST /v1/settlement/:id/resolve — waiving requires a reason. */
export const settleSchema = z
  .object({
    action: z.enum(['settle', 'waive']),
    reason: z.string().min(1).max(1000).optional(),
  })
  .refine((v) => v.action !== 'waive' || !!v.reason?.trim(), {
    message: 'Waiving a settlement requires a reason.',
    path: ['reason'],
  });
export type SettleInput = z.infer<typeof settleSchema>;
